import { FORM_ERROR } from "final-form";
import { observer } from "mobx-react-lite";
import { useContext, useMemo } from "react";

import { SideRailMenuItem, Stack } from "@bps/fluent-ui";
import { Country } from "@libs/enums/country.enum.ts";
import { Permission } from "@libs/gateways/core/CoreGateway.dtos.ts";
import { OrganisationRoleType } from "@libs/gateways/practice/PracticeGateway.dtos.ts";
import { PatientDemographicContext } from "@modules/practice/screens/contact-details/context/PatientDemographicContext.ts";
import { useStores } from "@stores/hooks/useStores.ts";
import { Contact } from "@stores/practice/models/Contact.ts";
import { withFetch } from "@ui-components/data-fetcher/DataFetcher.tsx";
import { When } from "@ui-components/withPerm.tsx";

import { Labels } from "../../../shared-components/types/labels.enums.types.ts";
import { OrganisationCardIds } from "../../../shared-components/types/organisation-card-ids.enum.ts";
import { ContactSubmissionFormDialog } from "../../shared-components/edit/ContactSubmissionFormDialog.tsx";
import { EditFormLayout } from "../../shared-components/edit/EditFormLayout.tsx";
import { EditProfile } from "../../shared-components/edit/EditProfile.tsx";
import { EditOrganisationalLocation } from "./EditOrganisationalLocation.tsx";
import { EditOrganisationBilling } from "./EditOrganisationBillingInfo.tsx";
import { EditOrganisationFormValues } from "./EditOrganisationFormValues.tsx";
import { EditOrganisationHeader } from "./EditOrganisationHeader.tsx";
import { EditOrganisationType } from "./EditOrganisationType.tsx";
import { EditOrgRelationships } from "./EditOrgRelationships.tsx";
import { getOrganisationFormValues, onSubmit, orgMenuItems } from "./utils.ts";
import { OrganisationFormValidator } from "./validators/OrganisationFormValidator.ts";

type OrganisationFormDialogProps = {
  organisation: Contact | undefined;
};

const OrganisationFormDialogBase: React.FC<OrganisationFormDialogProps> =
  observer(({ organisation }) => {
    const root = useStores();

    const { core, practice } = root;

    const {
      ui: { demographicInitialValues, newContactType }
    } = practice;

    const isNewOrganisationVisible = !!newContactType;

    const helper = useContext(PatientDemographicContext);

    const initialValues = useMemo(
      () =>
        getOrganisationFormValues(organisation, isNewOrganisationVisible, {
          core,
          practice
        }),
      [core, isNewOrganisationVisible, organisation, practice]
    );

    const orgMenuItemsEdit: SideRailMenuItem[] = [
      ...orgMenuItems,
      {
        text: Labels.profile,
        id: OrganisationCardIds.OrganisationProfile
      }
    ];

    const handleValidate = (formValues: EditOrganisationFormValues) => {
      const validator = new OrganisationFormValidator(
        practice.isNzPublicInsurer(initialValues.organisationRoles),
        initialValues.organisationName ?? "",
        {
          country: core.tenantDetails?.country ?? Country.Australia,
          countries: core.ref.countries.values
        }
      );
      return validator.validate(formValues, FORM_ERROR);
    };

    return (
      <ContactSubmissionFormDialog<EditOrganisationFormValues>
        initialValues={initialValues}
        validate={handleValidate}
        onSubmit={values =>
          onSubmit(organisation, { initialValues, values }, { helper, root })
        }
        buttonsProps={{
          hideButtonsSeparator: true,
          disableSubmitOnPristine: !demographicInitialValues,
          cancelButtonProps: {
            id: "close-organization-form-dialog"
          }
        }}
      >
        {() => (
          <EditFormLayout
            menuItems={
              isNewOrganisationVisible ? orgMenuItems : orgMenuItemsEdit
            }
            contact={organisation}
          >
            <Stack tokens={{ childrenGap: 48 }} grow>
              <EditOrganisationHeader organisation={organisation} />
              {organisation?.organisationRoles.some(
                x =>
                  x.organisationRoleTypeCode ===
                  OrganisationRoleType.NzPublicInsurer
              ) ? null : (
                <When permission={[Permission.SaveDraftAllowed]}>
                  <EditOrganisationBilling />
                </When>
              )}
              {practice.ref.organisationRoleType.values.length > 0 && (
                <EditOrganisationType />
              )}

              <EditOrganisationalLocation organisation={organisation} />
              <EditOrgRelationships />
              {!isNewOrganisationVisible && (
                <EditProfile
                  contact={organisation}
                  sectionId={OrganisationCardIds.OrganisationProfile}
                />
              )}
            </Stack>
          </EditFormLayout>
        )}
      </ContactSubmissionFormDialog>
    );
  });

export const OrganisationFormDialog = withFetch(
  x => [x.practice.ref.organisationRoleType.load()],
  OrganisationFormDialogBase
);
